import React, { createContext, ReactNode, useMemo, useState } from 'react';
import { Notification } from './Notification';

export type NotificationSeverity = 'success' | 'warning' | 'error';

export interface NotificationContextProps {
  open: boolean;
  message: string;
  severity: NotificationSeverity;
  showNotification: (
    message: string,
    severity: NotificationSeverity,
    skipTranslation: boolean,
  ) => void;
  hideNotification: () => void;
}

export const NotificationContext = createContext<NotificationContextProps>({
  open: false,
  message: '',
  severity: 'success',
  showNotification: (): void => undefined,
  hideNotification: (): void => undefined,
});

interface NotificationProviderProps {
  children: ReactNode; // Update the prop type to ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [skipTranslation, setSkipTranslation] = useState(false);
  const [severity, setSeverity] = useState<NotificationSeverity>('success');

  const showNotification = (
    messageContent: string,
    severityType: NotificationSeverity,
    skipTranslationValue: boolean,
  ): void => {
    setOpen(true);
    setMessage(messageContent);
    setSeverity(severityType);
    setSkipTranslation(skipTranslationValue);
  };

  const hideNotification = (): void => {
    setOpen(false);
  };

  const contextValue = useMemo(
    () => ({
      open,
      message,
      severity,
      skipTranslation,
      showNotification,
      hideNotification,
    }),
    [open, message, severity, skipTranslation],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <Notification
        open={open}
        message={message}
        severity={severity}
        skipTranslation={skipTranslation}
        onClose={hideNotification}
      />
    </NotificationContext.Provider>
  );
};
